import React, { useState, useEffect } from 'react';
import { Users, Package, PlusCircle, ChevronRight, Save, Trash2, AlertCircle } from 'lucide-react';
import { api } from '../services/apiClient';

const DistributorManager = () => {
  const [distributors, setDistributors] = useState([]);
  const [illustrations, setIllustrations] = useState([]);
  const [selectedDistributor, setSelectedDistributor] = useState(null);
  const [isAdding, setIsAdding] = useState(false);
  const [consignments, setConsignments] = useState([]);
  const [isAssigning, setIsAssigning] = useState(false);
  const [newDistributor, setNewDistributor] = useState({
    company_name: '', owner_name: '', phone: '', address: '', city: '', province: '', country: 'Italy'
  });
  const [newAssignment, setNewAssignment] = useState({
    illustration_id: '', quantity_sent: '', price_per_unit: '', delivery_date: new Date().toISOString().split('T')[0]
  });
  const [deleteConsignmentConfirm, setDeleteConsignmentConfirm] = useState(null);

  useEffect(() => {
    fetchDistributors();
    fetchIllustrations();
  }, []);

  useEffect(() => {
    if (selectedDistributor) {
      fetchConsignments(selectedDistributor.id);
    }
  }, [selectedDistributor]);

  async function fetchDistributors() {
    try {
      const data = await api.distributors.list();
      setDistributors(data);
    } catch (error) {
      console.error('Failed to fetch distributors:', error);
    }
  }

  async function fetchIllustrations() {
    try {
      const data = await api.illustrations.list();
      setIllustrations(data);
    } catch (error) {
      console.error('Failed to fetch illustrations:', error);
    }
  }

  async function fetchConsignments(distributorId) {
    try {
      const data = await api.consignments.listByDistributor(distributorId);
      setConsignments(data);
    } catch (error) {
      console.error('Failed to fetch consignments:', error);
    }
  }

  async function handleAddDistributor(e) {
    e.preventDefault();
    try {
      const data = await api.distributors.create(newDistributor);
      setDistributors([...distributors, data]);
      setIsAdding(false);
      setNewDistributor({ company_name: '', owner_name: '', phone: '', address: '', city: '', province: '', country: 'Italy' });
    } catch (error) {
      alert(`Errore: ${error.message}`);
    }
  }

  async function handleAssignIllustration(e) {
    e.preventDefault();
    const assignment = {
      ...newAssignment,
      distributor_id: selectedDistributor.id,
      quantity_sent: parseInt(newAssignment.quantity_sent),
      price_per_unit: parseFloat(newAssignment.price_per_unit)
    };
    try {
      await api.consignments.create(assignment);
      setIsAssigning(false);
      fetchConsignments(selectedDistributor.id);
      setNewAssignment({ illustration_id: '', quantity_sent: '', price_per_unit: '', delivery_date: new Date().toISOString().split('T')[0] });
    } catch (error) {
      alert(`Errore: ${error.message}`);
    }
  }

  async function updateSoldQuantity(id, qty) {
    try {
      await api.consignments.update(id, { quantity_sold: parseInt(qty) });
      fetchConsignments(selectedDistributor.id);
    } catch (error) {
      alert(`Errore: ${error.message}`);
    }
  }

  async function handleDeleteConsignment() {
    if (!deleteConsignmentConfirm) return;

    try {
      await api.consignments.delete(deleteConsignmentConfirm.id);
      setDeleteConsignmentConfirm(null);
      fetchConsignments(selectedDistributor.id);
    } catch (error) {
      alert(`Errore durante la cancellazione: ${error.message}`);
    }
  }

  const totalToCollect = consignments.reduce((acc, curr) => acc + ((curr.quantity_sold || 0) * curr.price_per_unit), 0);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-2 text-brand-dark">
          <Users size={24} />
          <h3 className="text-xl font-semibold">Gestione Distributori</h3>
        </div>
        {!isAdding && (
          <button
            onClick={() => setIsAdding(true)}
            className="flex items-center gap-2 bg-brand-dark text-white px-4 py-2 rounded-lg hover:bg-opacity-90 transition-all"
          >
            <PlusCircle size={18} /> Aggiungi Distributore
          </button>
        )}
      </div>

      {isAdding ? (
        <div className="bg-white p-6 rounded-xl border border-brand-main shadow-sm max-w-2xl mx-auto">
          <form onSubmit={handleAddDistributor} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-600 mb-1">Nome Azienda</label>
              <input className="w-full p-2 border border-gray-200 rounded-md focus:ring-2 focus:ring-brand-main outline-none" value={newDistributor.company_name} onChange={e => setNewDistributor({...newDistributor, company_name: e.target.value})} required />
            </div>
            <div className="col-span-2 flex justify-end gap-3 mt-4">
              <button type="button" onClick={() => setIsAdding(false)} className="px-4 py-2 text-gray-500">Annulla</button>
              <button type="submit" className="bg-brand-dark text-white px-6 py-2 rounded-lg hover:bg-opacity-90">Salva Distributore</button>
            </div>
          </form>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="bg-white rounded-xl border border-brand-main overflow-hidden shadow-sm h-fit">
            <div className="p-4 bg-gray-50 border-b border-brand-main font-medium">Elenco Distributori</div>
            <div className="divide-y divide-gray-100">
              {distributors.length === 0 && <p className="p-4 text-sm text-gray-400 text-center">Nessun distributore trovato.</p>}
              {distributors.map(d => (
                <button
                  key={d.id}
                  onClick={() => setSelectedDistributor(d)}
                  className={`w-full text-left p-4 hover:bg-brand-light transition-colors flex justify-between items-center ${selectedDistributor?.id === d.id ? 'bg-brand-light border-l-4 border-brand-main' : ''}`}
                >
                  <div>
                    <div className="font-medium text-brand-dark">{d.company_name}</div>
                    <div className="text-xs text-gray-500">{d.city} ({d.province})</div>
                  </div>
                  <ChevronRight size={16} className="text-gray-300" />
                </button>
              ))}
            </div>
          </div>

          <div className="lg:col-span-2">
            {selectedDistributor ? (
              <div className="bg-white p-6 rounded-xl border border-brand-main shadow-sm space-y-6">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="text-2xl font-bold text-brand-dark">{selectedDistributor.company_name}</h3>
                    <p className="text-gray-500">{selectedDistributor.owner_name} • {selectedDistributor.phone}</p>
                  </div>
                  <div className="text-right bg-brand-main bg-opacity-20 p-3 rounded-lg">
                    <p className="text-xs text-gray-500 uppercase font-bold">Da richiedere</p>
                    <p className="text-2xl font-bold text-brand-dark">€{totalToCollect.toFixed(2)}</p>
                  </div>
                </div>

                <div className="border-t border-brand-main pt-6">
                  <div className="flex justify-between items-center mb-4">
                    <h4 className="font-semibold flex items-center gap-2"><Package size={18} /> Opere in Conto Vendita</h4>
                    <button
                      onClick={() => setIsAssigning(true)}
                      className="text-sm bg-brand-main text-white px-3 py-1 rounded-md hover:bg-opacity-90 flex items-center gap-1"
                    >
                      <PlusCircle size={14} /> Affida Opera
                    </button>
                  </div>

                  {isAssigning && (
                    <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-dashed border-brand-main">
                      <form onSubmit={handleAssignIllustration} className="grid grid-cols-1 md:grid-cols-4 gap-3">
                        <div className="col-span-2">
                          <label className="block text-xs text-gray-500 mb-1">Scegli Opera</label>
                          <select
                            className="w-full p-2 border rounded-md text-sm"
                            value={newAssignment.illustration_id}
                            onChange={e => setNewAssignment({...newAssignment, illustration_id: e.target.value})}
                            required
                          >
                            <option value="">Seleziona...</option>
                            {illustrations.map(ill => (
                              <option key={ill.id} value={ill.id}>{ill.name}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Qtà</label>
                          <input type="number" className="w-full p-2 border rounded-md text-sm" value={newAssignment.quantity_sent} onChange={e => setNewAssignment({...newAssignment, quantity_sent: e.target.value})} required />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Prezzo</label>
                          <input type="number" step="0.01" className="w-full p-2 border rounded-md text-sm" value={newAssignment.price_per_unit} onChange={e => setNewAssignment({...newAssignment, price_per_unit: e.target.value})} required />
                        </div>
                        <div className="col-span-4 flex justify-end gap-2">
                          <button type="button" onClick={() => setIsAssigning(false)} className="text-xs text-gray-500 px-2">Annulla</button>
                          <button type="submit" className="text-xs bg-brand-dark text-white px-3 py-1 rounded-md flex items-center gap-1"><Save size={12}/> Salva</button>
                        </div>
                      </form>
                    </div>
                  )}

                  <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm">
                      <thead className="bg-gray-50 text-gray-500 uppercase text-xs">
                        <tr>
                          <th className="p-3 w-14">Foto</th>
                          <th className="p-3">Opera</th>
                          <th className="p-3 text-center">Inviate</th>
                          <th className="p-3 text-center">Vendute</th>
                          <th className="p-3 text-right">Totale</th>
                          <th className="p-3 text-center">Azione</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {consignments.length === 0 ? (
                          <tr><td colSpan="6" className="p-8 text-center text-gray-400 italic">Nessuna opera affidata a questo distributore.</td></tr>
                        ) : (
                          consignments.map(c => (
                            <tr key={c.id} className="hover:bg-gray-50">
                              <td className="p-3">
                                <div className="w-14 h-14 bg-gray-100 rounded-lg overflow-hidden border border-gray-200 flex items-center justify-center">
                                  <img src={c.illustrations?.image_url || 'https://via.placeholder.com/56'} alt={c.illustrations?.name} className="w-full h-full object-cover" />
                                </div>
                              </td>
                              <td className="p-3 font-medium">{c.illustrations?.name || 'Sconosciuta'}</td>
                              <td className="p-3 text-center">{c.quantity_sent}</td>
                              <td className="p-3 text-center">
                                <input
                                  type="number"
                                  className="w-16 p-1 border rounded text-center"
                                  value={c.quantity_sold || 0}
                                  onChange={(e) => updateSoldQuantity(c.id, e.target.value)}
                                />
                              </td>
                              <td className="p-3 text-right font-bold">€{( (c.quantity_sold || 0) * c.price_per_unit).toFixed(2)}</td>
                              <td className="p-3 text-center">
                                <button
                                  onClick={() => setDeleteConsignmentConfirm(c)}
                                  className="text-red-600 hover:text-red-800 transition-colors p-1 inline-block"
                                  title="Cancella associazione"
                                >
                                  <Trash2 size={16} />
                                </button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            ) : (
              <div className="h-full flex flex-col items-center justify-center text-gray-400 bg-gray-50 rounded-xl border border-dashed border-brand-main p-12">
                <Users size={48} className="mb-4 opacity-20" />
                <p>Seleziona un distributore per gestire le opere e i pagamenti</p>
              </div>
            )}
          </div>
        </div>
      )}

      {deleteConsignmentConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-sm shadow-lg">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-100 rounded-full">
                <AlertCircle size={24} className="text-red-600" />
              </div>
              <h3 className="text-lg font-bold text-brand-dark">Cancella Associazione</h3>
            </div>
            <p className="text-gray-600 mb-6">
              Sei sicuro di voler rimuovere <strong>{deleteConsignmentConfirm.illustrations?.name}</strong> dalla consegna a <strong>{selectedDistributor?.company_name}</strong>? Questa azione non può essere annullata.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteConsignmentConfirm(null)}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Annulla
              </button>
              <button
                onClick={handleDeleteConsignment}
                className="px-4 py-2 bg-red-600 text-white hover:bg-red-700 rounded-lg transition-colors flex items-center gap-2"
              >
                <Trash2 size={16} /> Cancella
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DistributorManager;
